import * as React from 'react';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { DndContext, DragEndEvent, DragOverEvent, PointerSensor, closestCorners, useDroppable, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    createLeagueCategory,
    deleteLeagueCategory,
    getLeagueCatalog,
    getPaginatedSports,
    saveLeagueCatalog,
    seedLeagueCatalog,
    updateCatalogLeague,
    updateLeagueCategory,
} from '../Api';
import LeagueCatalogDto, { LeagueCatalogItemDto, SaveLeagueCatalogRequest } from '../models/dtos/LeagueCatalogDto';
import SportEntityDto from '../models/dtos/SportEntityDto';

type ContainerId = 'featured' | 'uncategorized' | `category-${number}`;

function leagueSortableId(id: number) {
    return `league-${id}`;
}

function parseLeagueId(sortableId: string): number | null {
    if (!sortableId.startsWith('league-')) return null;
    const value = Number(sortableId.slice('league-'.length));
    return Number.isFinite(value) ? value : null;
}

function SortableLeagueRow(props: {
    league: LeagueCatalogItemDto;
    onRename: () => void;
}) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: leagueSortableId(props.league.id),
    });
    return (
        <Stack
            ref={setNodeRef}
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
                transform: CSS.Transform.toString(transform),
                transition,
                opacity: isDragging ? 0.5 : 1,
                py: 0.75,
                px: 1,
                borderRadius: 1,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
            }}
        >
            <Box sx={{ cursor: 'grab', color: 'text.disabled', display: 'flex' }} {...attributes} {...listeners}>
                <DragHandleIcon fontSize="small" />
            </Box>
            <Avatar src={props.league.photo_url ?? undefined} sx={{ width: 28, height: 28 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap variant="body2">{props.league.display_name || props.league.name}</Typography>
                {props.league.display_name ? (
                    <Typography noWrap variant="caption" color="text.secondary">{props.league.name}</Typography>
                ) : null}
            </Box>
            <IconButton size="small" onClick={props.onRename}><EditIcon fontSize="small" /></IconButton>
        </Stack>
    );
}

function DroppableSection(props: {
    id: ContainerId;
    title: string;
    photoUrl?: string | null;
    children: React.ReactNode;
    actions?: React.ReactNode;
    leagueIds: string[];
}) {
    const { setNodeRef } = useDroppable({ id: props.id });
    return (
        <Card ref={setNodeRef} variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                    {props.photoUrl ? <Avatar src={props.photoUrl} sx={{ width: 32, height: 32 }} /> : null}
                    <Typography variant="h6" sx={{ flex: 1 }}>{props.title}</Typography>
                    {props.actions}
                </Stack>
                <SortableContext items={props.leagueIds} strategy={verticalListSortingStrategy}>
                    <Stack spacing={0.75} minHeight={48}>
                        {props.children}
                    </Stack>
                </SortableContext>
            </CardContent>
        </Card>
    );
}

function toSaveRequest(catalog: LeagueCatalogDto): SaveLeagueCatalogRequest {
    return {
        featuredLeagueIds: catalog.featured.map(item => item.id),
        categories: catalog.categories.map(category => ({
            id: category.id,
            leagueIds: category.leagues.map(item => item.id),
        })),
        uncategorizedLeagueIds: catalog.uncategorized.map(item => item.id),
    };
}

function allLeagues(catalog: LeagueCatalogDto): LeagueCatalogItemDto[] {
    return [
        ...catalog.featured,
        ...catalog.categories.flatMap(category => category.leagues),
        ...catalog.uncategorized,
    ];
}

function findContainer(catalog: LeagueCatalogDto, leagueId: number): ContainerId | null {
    if (catalog.featured.some(item => item.id === leagueId)) return 'featured';
    if (catalog.uncategorized.some(item => item.id === leagueId)) return 'uncategorized';
    const category = catalog.categories.find(item => item.leagues.some(league => league.id === leagueId));
    return category ? `category-${category.id}` : null;
}

function leaguesIn(catalog: LeagueCatalogDto, container: ContainerId): LeagueCatalogItemDto[] {
    if (container === 'featured') return catalog.featured;
    if (container === 'uncategorized') return catalog.uncategorized;
    const categoryId = Number(container.slice('category-'.length));
    return catalog.categories.find(category => category.id === categoryId)?.leagues ?? [];
}

function setLeaguesIn(catalog: LeagueCatalogDto, container: ContainerId, leagues: LeagueCatalogItemDto[]): LeagueCatalogDto {
    if (container === 'featured') return { ...catalog, featured: leagues };
    if (container === 'uncategorized') return { ...catalog, uncategorized: leagues };
    const categoryId = Number(container.slice('category-'.length));
    return {
        ...catalog,
        categories: catalog.categories.map(category => category.id === categoryId ? { ...category, leagues } : category),
    };
}

const DEFAULT_SPORT_ID = 22;

const CONTINENT_ORDER = [
    'World',
    'Europe',
    'South America',
    'North America',
    'Africa',
    'Asia',
    'Oceania',
    'Caribbean',
];

function compareLabels(left: string, right: string) {
    return left.localeCompare(right, undefined, { sensitivity: 'base', numeric: true });
}

function continentRank(name: string) {
    const index = CONTINENT_ORDER.findIndex(item => item.toLowerCase() === name.trim().toLowerCase());
    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
}

function leagueLabel(league: LeagueCatalogItemDto) {
    return (league.display_name || league.name || '').trim();
}

function sortLeaguesAz(leagues: LeagueCatalogItemDto[]) {
    return [...leagues].sort((left, right) => compareLabels(leagueLabel(left), leagueLabel(right)));
}

function sortCatalogAlphabetically(catalog: LeagueCatalogDto): LeagueCatalogDto {
    const continents = catalog.categories
        .filter(category => continentRank(category.name) < Number.MAX_SAFE_INTEGER)
        .sort((left, right) => continentRank(left.name) - continentRank(right.name) || compareLabels(left.name, right.name));
    const countries = catalog.categories
        .filter(category => continentRank(category.name) === Number.MAX_SAFE_INTEGER)
        .sort((left, right) => compareLabels(left.name, right.name));
    return {
        ...catalog,
        categories: [...continents, ...countries].map(category => ({
            ...category,
            leagues: sortLeaguesAz(category.leagues),
        })),
        uncategorized: sortLeaguesAz(catalog.uncategorized),
    };
}

export default function LeagueCatalogPage() {
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { delay: 180, tolerance: 8 } }));
    const [sports, setSports] = React.useState<SportEntityDto[]>([]);
    const [sportId, setSportId] = React.useState<number | ''>('');
    const [catalog, setCatalog] = React.useState<LeagueCatalogDto | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [message, setMessage] = React.useState<string | null>(null);
    const [categoryDialog, setCategoryDialog] = React.useState<{ id?: number; name: string; photo_url: string } | null>(null);
    const [leagueDialog, setLeagueDialog] = React.useState<{ id: number; display_name: string } | null>(null);
    const [deleteCategoryId, setDeleteCategoryId] = React.useState<number | null>(null);
    const [seeding, setSeeding] = React.useState(false);

    React.useEffect(() => {
        getPaginatedSports(0, 200).then(result => {
            const items = result?.items ?? [];
            setSports(items);
            if (items.length > 0) {
                setSportId(items.some(item => item.id === DEFAULT_SPORT_ID) ? DEFAULT_SPORT_ID : items[0].id);
            }
        });
    }, []);

    const loadCatalog = React.useCallback(async (id: number) => {
        setLoading(true);
        setMessage(null);
        const data = await getLeagueCatalog(id);
        setCatalog(data);
        setLoading(false);
        if (!data) {
            setMessage('Could not load league catalog. Apply migration V9 and restart analytics.');
        }
    }, []);

    React.useEffect(() => {
        if (sportId === '') return;
        loadCatalog(sportId);
    }, [sportId, loadCatalog]);

    const persist = async (next: LeagueCatalogDto) => {
        if (sportId === '') return;
        setSaving(true);
        const saved = await saveLeagueCatalog(sportId, toSaveRequest(next));
        setSaving(false);
        if (saved) {
            setCatalog(saved);
        } else {
            setMessage('Could not save league order.');
            loadCatalog(sportId);
        }
    };

    const moveLeague = (current: LeagueCatalogDto, leagueId: number, to: ContainerId, overLeagueId: number | null) => {
        const from = findContainer(current, leagueId);
        if (from == null) return current;
        const league = allLeagues(current).find(item => item.id === leagueId);
        if (!league) return current;
        let next = setLeaguesIn(current, from, leaguesIn(current, from).filter(item => item.id !== leagueId));
        const destination = [...leaguesIn(next, to)];
        const overIndex = overLeagueId == null ? destination.length : destination.findIndex(item => item.id === overLeagueId);
        const insertAt = overIndex < 0 ? destination.length : overIndex;
        destination.splice(insertAt, 0, league);
        return setLeaguesIn(next, to, destination);
    };

    const onDragOver = (event: DragOverEvent) => {
        if (!catalog) return;
        const activeId = parseLeagueId(String(event.active.id));
        if (activeId == null) return;
        const overId = event.over?.id == null ? null : String(event.over.id);
        if (overId == null) return;
        const overLeagueId = parseLeagueId(overId);
        const to: ContainerId | null = overLeagueId != null
            ? findContainer(catalog, overLeagueId)
            : (overId as ContainerId);
        if (to == null) return;
        const from = findContainer(catalog, activeId);
        if (from == null || from === to) return;
        setCatalog(moveLeague(catalog, activeId, to, overLeagueId));
    };

    const onDragEnd = async (event: DragEndEvent) => {
        if (!catalog) return;
        const activeId = parseLeagueId(String(event.active.id));
        if (activeId == null) return;
        const overId = event.over?.id == null ? null : String(event.over.id);
        if (overId == null) {
            await persist(catalog);
            return;
        }
        const overLeagueId = parseLeagueId(overId);
        const from = findContainer(catalog, activeId);
        const to = overLeagueId != null ? findContainer(catalog, overLeagueId) : overId as ContainerId;
        if (from == null || to == null) {
            await persist(catalog);
            return;
        }
        let next = catalog;
        if (from === to && overLeagueId != null) {
            const ids = leaguesIn(catalog, from).map(item => item.id);
            const oldIndex = ids.indexOf(activeId);
            const newIndex = ids.indexOf(overLeagueId);
            if (oldIndex >= 0 && newIndex >= 0 && oldIndex !== newIndex) {
                next = setLeaguesIn(catalog, from, arrayMove(leaguesIn(catalog, from), oldIndex, newIndex));
                setCatalog(next);
            }
        }
        await persist(next);
    };

    const onSortAlphabetically = async () => {
        if (sportId === '' || !catalog) return;
        await persist(sortCatalogAlphabetically(catalog));
    };

    const onImportFromProvider = async () => {
        if (sportId === '') return;
        setSeeding(true);
        setMessage(null);
        const data = await seedLeagueCatalog(sportId);
        setSeeding(false);
        if (data) {
            setCatalog(data);
        } else {
            setMessage('Could not import countries from 1xBet. Apply migration V9 and restart analytics.');
        }
    };

    const onCreateCategory = async () => {
        if (sportId === '' || !categoryDialog || categoryDialog.id != null) return;
        const created = await createLeagueCategory(sportId, { name: categoryDialog.name, photo_url: categoryDialog.photo_url || null });
        setCategoryDialog(null);
        if (created && catalog) {
            setCatalog({ ...catalog, categories: [...catalog.categories, { ...created, leagues: created.leagues ?? [] }] });
        } else {
            loadCatalog(sportId);
        }
    };

    const onSaveCategory = async () => {
        if (sportId === '' || !categoryDialog?.id) return;
        await updateLeagueCategory(sportId, categoryDialog.id, { name: categoryDialog.name, photo_url: categoryDialog.photo_url || null });
        setCategoryDialog(null);
        loadCatalog(sportId);
    };

    const confirmDeleteCategory = async () => {
        if (sportId === '' || deleteCategoryId == null) return;
        const categoryId = deleteCategoryId;
        setDeleteCategoryId(null);
        await deleteLeagueCategory(sportId, categoryId);
        loadCatalog(sportId);
    };

    const onSaveLeagueName = async () => {
        if (sportId === '' || !leagueDialog) return;
        await updateCatalogLeague(sportId, leagueDialog.id, { display_name: leagueDialog.display_name || null });
        setLeagueDialog(null);
        loadCatalog(sportId);
    };

    return (
        <Box sx={{ p: 3 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
                <Typography variant="h5" sx={{ flex: 1 }}>League catalog</Typography>
                <FormControl sx={{ minWidth: 260 }}>
                    <InputLabel>Sport</InputLabel>
                    <Select
                        label="Sport"
                        value={sportId}
                        onChange={event => setSportId(event.target.value as number)}
                    >
                        {sports.map(sport => (
                            <MenuItem key={sport.id} value={sport.id}>{sport.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button
                    variant="outlined"
                    startIcon={<SortByAlphaIcon />}
                    disabled={sportId === '' || catalog == null || saving || loading}
                    onClick={onSortAlphabetically}
                >
                    Sort A–Z
                </Button>
                <Button variant="outlined" disabled={sportId === '' || seeding} onClick={onImportFromProvider}>
                    {seeding ? 'Importing…' : 'Import from 1xBet'}
                </Button>
                <Button variant="contained" startIcon={<AddIcon />} disabled={sportId === ''} onClick={() => setCategoryDialog({ name: '', photo_url: '' })}>
                    Add country
                </Button>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Only leagues with a live match or a match scheduled in the next 30 days are listed, same set the app shows.
                The app lists Featured as you pinned them, then World and continents, then countries A–Z, Other last.
                Leagues inside a country are A–Z in the app, not by popularity. Sort A–Z updates this catalog page to the
                same order. Archive leagues stay in the database and are not rearranged.
            </Typography>
            {message ? <Alert severity="warning" sx={{ mb: 2 }}>{message}</Alert> : null}
            {saving ? <Typography variant="caption" color="text.secondary">Saving…</Typography> : null}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
            ) : catalog == null ? (
                <Typography variant="body2" color="text.secondary">No catalog loaded.</Typography>
            ) : (
                <DndContext sensors={sensors} collisionDetection={closestCorners} onDragOver={onDragOver} onDragEnd={onDragEnd}>
                    <DroppableSection id="featured" title={`Featured / world (${catalog.featured.length})`} leagueIds={catalog.featured.map(item => leagueSortableId(item.id))}>
                        {catalog.featured.map(league => (
                            <SortableLeagueRow key={league.id} league={league} onRename={() => setLeagueDialog({ id: league.id, display_name: league.display_name || '' })} />
                        ))}
                    </DroppableSection>
                    {catalog.categories.map(category => (
                        <DroppableSection
                            key={category.id}
                            id={`category-${category.id}`}
                            title={`${category.name} (${category.leagues.length})`}
                            photoUrl={category.photo_url}
                            leagueIds={category.leagues.map(item => leagueSortableId(item.id))}
                            actions={
                                <Stack direction="row">
                                    <IconButton size="small" onClick={() => setCategoryDialog({ id: category.id, name: category.name, photo_url: category.photo_url || '' })}><EditIcon fontSize="small" /></IconButton>
                                    <IconButton size="small" onClick={() => setDeleteCategoryId(category.id)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                                </Stack>
                            }
                        >
                            {category.leagues.map(league => (
                                <SortableLeagueRow key={league.id} league={league} onRename={() => setLeagueDialog({ id: league.id, display_name: league.display_name || '' })} />
                            ))}
                        </DroppableSection>
                    ))}
                    <DroppableSection id="uncategorized" title={`Uncategorized (${catalog.uncategorized.length})`} leagueIds={catalog.uncategorized.map(item => leagueSortableId(item.id))}>
                        {catalog.uncategorized.map(league => (
                            <SortableLeagueRow key={league.id} league={league} onRename={() => setLeagueDialog({ id: league.id, display_name: league.display_name || '' })} />
                        ))}
                    </DroppableSection>
                </DndContext>
            )}

            <Dialog open={categoryDialog != null} onClose={() => setCategoryDialog(null)} fullWidth maxWidth="sm">
                <DialogTitle>{categoryDialog?.id ? 'Edit country' : 'Add country'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField label="Name" value={categoryDialog?.name ?? ''} onChange={event => setCategoryDialog(current => current ? { ...current, name: event.target.value } : current)} />
                        <TextField label="Icon URL" value={categoryDialog?.photo_url ?? ''} onChange={event => setCategoryDialog(current => current ? { ...current, photo_url: event.target.value } : current)} />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCategoryDialog(null)}>Cancel</Button>
                    <Button
                        variant="contained"
                        disabled={!categoryDialog?.name.trim()}
                        onClick={categoryDialog?.id ? onSaveCategory : onCreateCategory}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={leagueDialog != null} onClose={() => setLeagueDialog(null)} fullWidth maxWidth="sm">
                <DialogTitle>League display name</DialogTitle>
                <DialogContent>
                    <TextField
                        sx={{ mt: 1 }}
                        fullWidth
                        label="Display name"
                        helperText="Leave empty to keep the 1xBet name. 1xBet sync will not overwrite this."
                        value={leagueDialog?.display_name ?? ''}
                        onChange={event => setLeagueDialog(current => current ? { ...current, display_name: event.target.value } : current)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLeagueDialog(null)}>Cancel</Button>
                    <Button variant="contained" onClick={onSaveLeagueName}>Save</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteCategoryId != null} onClose={() => setDeleteCategoryId(null)} fullWidth maxWidth="xs">
                <DialogTitle>Delete country</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Leagues in this country move to Uncategorized. They will not jump into Featured.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteCategoryId(null)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={confirmDeleteCategory}>Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
